import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback
} from 'react';
import { string, bool, shape, number } from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { Carousel } from '@thd-olt-component-react/core-ui';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import {
  useDataModel,
  extend,
  params,
  string as dString,
  shape as dShape,
  arrayOf,
  QueryProvider
} from '@thd-nucleus/data-sources';

import { SponsoredProductPod } from '../SponsoredProductPod';
import { makeApiRequest, shouldMakeApiRequest } from '../../utils/requestHelpers';
import {
  getSchema,
  getSkus,
  isExperienceDataResolved
} from '../../utils/helpers';

import './SponsoredCarousel.scss';

const SponsoredCarouselComponent = (props) => {
  const { pageContext, hideTitle, slidesPer, noATCFulfillment } = props;
  const experienceContext = useContext(ExperienceContext);
  let { renderNewCarousel = false } = useLayoutGroupProps({ props });
  const { storeId, isLocalized } = useStore({
    varnish: true,
    online: true
  });

  let breakpoints = {};
  if (!slidesPer) {
    breakpoints = Carousel.defaultProps.breakpoints;
  } else {
    breakpoints = {
      sm: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      md: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      lg: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
      xl: { slidesPerView: slidesPer, slidesPerGroup: slidesPer },
    };
  }

  const isMobile = useMemo(() => {
    let agentMobileMatches;
    if (typeof window !== 'undefined') {
      agentMobileMatches = window.navigator.userAgent.match(/iPhone|(Android)|THDConsumer/g);
    }
    return !!agentMobileMatches;
  });
  const isDesktop = useMemo(() => !isMobile, [isMobile]);
  const { schema, schemaId } = useMemo(() => {
    const schemaName = getSchema({ schema: pageContext.schema, pageContext });
    return { schema: schemaName, schemaId: schemaName + '__carousel' };
  }, [props]);
  const titleClassname = useMemo(() => {
    return `nucleus-carousel__title__text ${isMobile ? 'nucleus-carousel__title__text--medium' : ''}`;
  }, [isMobile]);

  const [lastRequestScenario, setLastRequestScenario] = useState({});
  const [pageId, setPageId] = useState('');
  const [products, setProducts] = useState([]);

  const setAdProducts = useCallback((adProducts) => {
    if (adProducts && adProducts.length) {
      setProducts(adProducts
        .slice(0, 16)
        .map((product) => ({
          ...product,
          onViewBeacon: product.onViewBeacon ? product.onViewBeacon.replace('https:', '') : '',
          ITC: `AUC-${product?.campaignId || ''}-${product?.placementId || ''}-${product?.slotId || ''}`
        }))
      );
    }
  }, []);

  if (schema === 'hp_sponsored_auth') {
    renderNewCarousel = true;
  }

  useEffect(() => {
    const isResolvedContext = isExperienceDataResolved({ isLocalized, experienceContext });

    if (renderNewCarousel && isResolvedContext) {

      if (pageContext?.label === 'browse-search' || pageContext.isPip) {
        const isValidScenario = shouldMakeApiRequest({ lastRequestScenario, experienceContext, pageContext });

        if (isValidScenario) {
          setLastRequestScenario({
            url: window.location.pathname + window.location.search,
            skus: getSkus(pageContext)
          });

          makeApiRequest({
            schema,
            schemaId,
            experienceContext,
            pageContext,
            carouselType: pageContext.isPip ? 'pip' : 'pla',
            setPageId,
            setAdProducts,
          });
        }
      } else {
        // Personalized Homepage should by pass isValidScenario
        makeApiRequest({
          schema,
          schemaId,
          experienceContext,
          pageContext,
          carouselType: 'pla',
          setPageId,
          setAdProducts
        });
      }
    }
  }, [pageContext.data, isLocalized, experienceContext.mounted]);

  const { data } = useDataModel('searchModel', {
    ssr: false,
    skip: !products?.length,
    variables: {
      itemIds: products?.map((item) => item.id.toString()) || [],
      storeId,
    }
  });

  return (
    renderNewCarousel && (
      <div id={schema} className="nucleus-carousel">
        {!!products?.length && !!data?.searchModel?.products?.length && (
          <>
            <meta data-prop="name" content={schema} />
            {!hideTitle && (
              <div className="nucleus-carousel__title">
                <h2 className={titleClassname}>
                  Sponsored Products
                </h2>
              </div>
            )}
            <Carousel breakpoints={breakpoints}>
              {products.map((product, idx) => (
                <SponsoredProductPod
                  key={product.id}
                  product={product}
                  pageId={pageId}
                  storeId={storeId}
                  isDesktop={isDesktop}
                  position={idx + 1}
                  schema={schema}
                  noATCFulfillment={noATCFulfillment}
                />
              ))}
            </Carousel>
          </>
        )}
      </div>
    )
  );
};

/* eslint react/jsx-props-no-spreading: 0 */
const SponsoredCarousel = (props) => (
  <QueryProvider cacheKey="sponsored-nucleus-carousel">
    <SponsoredCarouselComponent {...props} />
  </QueryProvider>
);

SponsoredCarouselComponent.propTypes = {
  hideTitle: bool,
  pageContext: shape({
    label: string,
    data: shape(),
    keyword: string,
    isSearch: bool,
    isCategory: bool,
    isBrowse: bool,
    schema: string
  }),
  slidesPer: number,
  noATCFulfillment: bool
};

SponsoredCarouselComponent.defaultProps = {
  hideTitle: false,
  pageContext: {},
  slidesPer: null,
  noATCFulfillment: null
};

SponsoredCarouselComponent.dataModel = extend({
  searchModel: params({
    itemIds: arrayOf(dString()),
  }).shape({
    products: arrayOf(dShape(ProductPod.dataModel.product))
  })
});

export { SponsoredCarousel };
